/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Copy, PlusCircle } from '@phosphor-icons/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useReactFlow } from 'reactflow';
import { useAuthStore } from '../../../store/storeAuth';
import { useNotificationStore } from '../../../store/storeNotifications';
import IconButtonTooltip from '../../UI/IconButtonTooltip/IconButtonTooltip';
import KitModal from '../../UI/KitModal';
import Label from '../../UI/Label/Label';

import JsonView from 'react18-json-view';
import 'react18-json-view/src/style.css';
import { FluxService } from '../../../service/FluxService';
import useFlowsStore from '../../../store/storeFlows';

import 'react18-json-view/src/dark.css';
import { useSessionStore } from '../../../store/storeSessions';
import SmallMemorySessionModal from '../../SmallMemory/SmallMemorySessionModal';

type Props = {
  onHide: () => void;
  show: boolean;
  formData: any;
  onChange: (e: any) => void;
  flowId?: string;
  isPublic?: boolean;
  isWebhook?: boolean;
  onChangeIsPublicOrWebhook?: (e: any) => void;
  spaceId?: string;
  onChangeSessionId?: (e: any) => void;
  onCreateNewSession: (name: string) => void;
};

const SettingsModal = ({
  onHide,
  show,
  formData,
  onChange,
  flowId,
  isPublic,
  isWebhook,
  onChangeIsPublicOrWebhook,
  onCreateNewSession,
}: Props) => {
  const { getNodes, getEdges } = useReactFlow();
  const { flows, updateFlow } = useFlowsStore(state => state);
  const { user } = useAuthStore(state => state);
  const [flowSummary, setFlowSummary] = useState<string>(formData.summary || '');
  const [isLoadingSummary, setIsLoadingSummary] = useState<boolean>(false);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [summaryJsonExpanded, setSummaryJsonExpanded] = useState<boolean>(false);
  const [originalSummary, setOriginalSummary] = useState<string>(formData.summary || '');
  const [userEdited, setUserEdited] = useState<boolean>(false);
  const [showJson, setShowJson] = useState<boolean>(false);
  const [showFilesExample, setShowFilesExample] = useState(false);
  const [showAgentSessionModal, setShowAgentSessionModal] = useState<boolean>(false);
  const [sessionName, setSessionName] = useState<string>('');

  const setNotification = useNotificationStore(state => state.setNotification);

  const {
    activeSession,
    sessions,
    setActiveSession,
  } = useSessionStore(state => state);

  const activeFlow = flows.find(flow => flow.id === flowId);

  useEffect(() => {
    if (formData.summary && formData.summary !== flowSummary) {
      setFlowSummary(formData.summary);
      setOriginalSummary(formData.summary);
      setUserEdited(false);
    }
  }, [formData.summary, flowSummary]);

  const flowJson = useMemo(() => {
    const nodes = getNodes();
    const edges = getEdges();
    const flowData = { nodes, edges };
    return JSON.stringify(flowData, null, 2);
  }, [getNodes, getEdges]);

  const config = useMemo(() => {
    let nodes = getNodes();
    const getAllVarInputNodes = () => {
      const varInputNodes = nodes?.filter(
        node => node?.type === 'varInputNode',
      );

      if (!varInputNodes.length) {
        return 'No var input nodes found';
      }

      const rawJson = JSON.stringify(
        {
          // FIELD DOCUMENTATION
          _documentation: {
            description: "Structure of input data for the flow",
            fields: {
              inputId: "Unique identifier of the input node (required)",
              inputText: "Text or content that will be processed by the flow (required)",
              links: "Array of links related to the input (optional)",
              files: "Array of files attached to the input (optional)",
              isExtraction: "Boolean: indicates if this input is for data extraction (optional, options: true, false)",
              typeOCR: "String: type of OCR applied to the input (optional, default: 'mistral_ocr', options: 'mistral_ocr', 'mistral_vision', 'google_ocr', 'google_ocr_form_parse')"
            },
            nestedFields: {
              links: {
                name: "Descriptive name of the link (optional)",
                url: "Complete URL of the link (optional)"
              },
              files: {
                name: "File name with extension (optional)",
                base64: "File content encoded in base64 (optional)"
              }
            }
          },
          // REAL DATA
          variableInputs: varInputNodes.map((node, idx) => ({
            inputId: node?.id,
            inputText: node?.data.text,
          }))
        },
        null,
        2
      );

      return rawJson;
    };
    return getAllVarInputNodes();
  }, [getNodes]);

  const configFull = useMemo(() => {
    let nodes = getNodes();
    const getAllVarInputNodes = () => {
      const varInputNodes = nodes?.filter(
        node => node?.type === 'varInputNode',
      );

      if (!varInputNodes.length) {
        return 'No var input nodes found';
      }

      const rawJson = JSON.stringify(
        {
          variableInputs: varInputNodes.map((node, idx) => ({
            inputId: node?.id,
            inputText: node?.data.text,
            "links": [
              {
                "name": "Descriptive name of the link (optional)",
                "url": "File URL (optional)"
              }
            ],
            "files": [
              {
                "name": "File name with extension (optional)",
                "base64": "File content encoded in base64 (optional)"
              }
            ],
            "isExtraction": "Boolean: indicates if this input is for data extraction (optional, options: true, false)",
            "typeOCR": "String: type of OCR applied to the input (optional, default: 'mistral_ocr', options: 'mistral_ocr', 'vision_ocr', 'google_ocr')"
          }))
        },
        null,
        2
      );

      return rawJson;
    };
    return getAllVarInputNodes();
  }, [getNodes]);

  const nodeOptionsApiInput = useMemo(() => {
    let nodes = getNodes();
    const getAllVarInputNodes = () => {
      const varInputNodes = nodes?.filter(
        node => node?.type === 'varInputNode',
      );

      if (!varInputNodes.length) {
        return [];
      }

      return varInputNodes.map(node => ({
        value: node?.id,
        label: node?.data?.label,
      }));
    };
    return getAllVarInputNodes();
  }, [getNodes]);

  const apiKey = user?.apiKey;

  const copyFlow = () => {
    navigator.clipboard.writeText(activeFlow?.id || '');
    setNotification({
      message: 'Flow ID copied to clipboard',
      type: 'success',
    });
  };

  const copyFlowEmail = () => {
    navigator.clipboard.writeText(
      `${activeFlow?.id}@${process.env.REACT_APP_FLOW_EMAIL}`,
    );
    setNotification({
      message: 'Flow Email copied to clipboard',
      type: 'success',
    });
  };

  const copyWebhookUrl = () => {
    navigator.clipboard.writeText(
      `${process.env.REACT_APP_API_URL}/flux/api-v2-webhook?flowId=${activeFlow?.id}`,
    );
    setNotification({
      message: 'Flow Webhook URL copied to clipboard',
      type: 'success',
    });
  };

  const copyFlowJson = () => {
    navigator.clipboard.writeText(flowJson);
    setNotification({
      message: 'Flow JSON copied to clipboard',
      type: 'success',
    });
  };

  const generateFlowSummary = useCallback(async () => {
    if (flowSummary && !isLoadingSummary) {
      const confirmRegenerate = window.confirm(
        "Are you sure you want to regenerate the summary? This will overwrite your current summary."
      );
      if (!confirmRegenerate) {
        return;
      }
    }

    setIsLoadingSummary(true);
    setSummaryError(null);

    try {
      const nodes = getNodes();
      const edges = getEdges();
      const flowData = { nodes, edges };
      const flowDataString = JSON.stringify(flowData, null, 2);

      const truncateJsonValues = (obj: any, maxLength = 300): any => {
        if (!obj) return obj;

        if (typeof obj === 'object') {
          if (Array.isArray(obj)) {
            return obj.map(item => truncateJsonValues(item, maxLength));
          } else {
            const result: Record<string, any> = {};
            for (const key in obj) {
              result[key] = truncateJsonValues(obj[key], maxLength);
            }
            return result;
          }
        } else if (typeof obj === 'string' && obj.length > maxLength) {
          return obj.substring(0, maxLength) + '...';
        }

        return obj;
      };

      const parsedFlowData = JSON.parse(flowDataString);
      const truncatedFlowData = truncateJsonValues(parsedFlowData);
      const truncatedFlowDataString = JSON.stringify(truncatedFlowData, null, 2);

      const summaryFlowId = process.env.REACT_APP_SUMMARY_FLOW_ID || '';

      const variableInputs = [
        {
          inputId: "varInputNode_1747428197481_0.4927",
          inputText: truncatedFlowDataString,
          links: [],
          files: []
        }
      ];


      const response = await FluxService.FluxApiProduction(summaryFlowId, {
        variableInputs
      });


      if (response?.data?.message && typeof response.data.message === 'string') {
        console.error('API returned an error:', response.data.message);
        setSummaryError(`API Error: ${response.data.message}`);
        setNotification({
          type: 'error',
          message: `Error generating flow summary: ${response.data.message}`,
          duration: 5000,
        });
        return;
      }

      let summaryText = 'No summary available';

      if (response?.data?.message &&
        Array.isArray(response.data.message) &&
        response.data.message.length > 0) {

        const output = response.data.message.find(
          (output: { outputId: string }) =>
            output.outputId === "outputObjectNode_1747166015458_0.8055"
        );

        if (output) {
          summaryText = output.text || summaryText;
        } else {
          summaryText = response.data.message[0].text || summaryText;
        }
      }

      setFlowSummary(summaryText);
      setOriginalSummary(summaryText);
      setUserEdited(false);

      if (summaryText !== 'No summary available') {

        onChange({
          target: {
            name: 'summary',
            value: summaryText
          }
        });
      }

      setNotification({
        type: 'success',
        message: 'Flow summary generated successfully',
        duration: 3000,
      });
    } catch (error: any) {
      console.error('Error generating flow summary:', error);

      let errorMessage = error instanceof Error ? error.message : String(error);
      try {
        if (error.response && error.response.data) {
          errorMessage = error.response.data.message || errorMessage;
        }
      } catch (e) {
      }

      setSummaryError(`Error: ${errorMessage}`);
      setNotification({
        type: 'error',
        message: `Error generating flow summary: ${errorMessage}`,
        duration: 5000,
      });
    } finally {
      setIsLoadingSummary(false);
    }
  }, [getNodes, getEdges, setNotification, onChange]);

  useEffect(() => {
    const shouldGenerateSummary = show &&
      !flowSummary &&
      !formData.summary &&
      !isLoadingSummary;

    if (shouldGenerateSummary) {
      generateFlowSummary();
    }
  }, [show, flowSummary, isLoadingSummary, generateFlowSummary, formData.summary]);

  const generateLinkForFlowId = useMemo(() => {
    if (window.location.origin.includes('localhost')) {
      return `http://localhost:3001/flux/api-v2?flowId=${flowId}`;
    }

    if (window.location.origin.includes('staging')) {
      return `https://api-staging.fluxprompt.ai/flux/api-v2?flowId=${flowId}`;
    }

    if (window.location.origin.includes('app')) {
      return `https://api.fluxprompt.ai/flux/api-v2?flowId=${flowId}`;
    }

    if (window.location.origin.includes('dev')) {
      return `https://api-dev.fluxprompt.ai/flux/api-v2?flowId=${flowId}`;
    }
  }, [flowId]);

  const isValidJson = useCallback((str: string) => {
    try {
      JSON.parse(str);
      return true;
    } catch (e) {
      return false;
    }
  }, []);

  return (
    <KitModal
      fullWidth
      sx={{
        '& .MuiPaper-root': {
          maxWidth: '690px',
          borderRadius: '16px',
        },
        '& .MuiDialogContent-root': {
          padding: '16px',
        },
        '& .MuiDialogActions-root': {
          padding: '16px',
        },
      }}
      showCloseButton={false}
      onHide={onHide}
      show={show}
    >
      <Box>
        <Label isAdvanced labelName="Details:" />
        <Divider
          sx={{
            mb: '8px',
            mt: '8px',
          }}
        />
        <Typography
          sx={{
            color: '#475467',
            fontSize: '16px',
            mb: '4px',
          }}
        >
          Flow Name:{' '}
          <span
            style={{
              color: '#98A2B3',
            }}
          >
            {activeFlow?.name}
          </span>
        </Typography>

        <Typography
          sx={{
            color: '#475467',
            fontSize: '16px',
            mb: '4px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
          }}
        >
          Flow ID:{' '}
          <span
            style={{
              color: '#98A2B3',
            }}
          >
            {activeFlow?.id}
          </span>
          <IconButtonTooltip title="Copy Flow Id" onClick={copyFlow}>
            <Copy size={14} color="#475467" />
          </IconButtonTooltip>
        </Typography>

        <Box sx={{ mt: '16px' }}>
          <Label isAdvanced labelName="Flow Information:" />
          <Divider
            sx={{
              mb: '8px',
              mt: '8px',
            }}
          />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: '8px' }}>
            <FormLabel
              sx={{
                color: '#475467',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
              htmlFor="flowSummary"
            >
              Flow Summary
              <IconButtonTooltip title="Copy Flow Summary" onClick={() => {
                navigator.clipboard.writeText(flowSummary);
                setNotification({
                  message: 'Flow summary copied to clipboard',
                  type: 'success',
                });
              }}>
                <Copy size={14} color="#475467" />
              </IconButtonTooltip>
            </FormLabel>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {flowSummary && isValidJson(flowSummary) && (
                <Button
                  variant="text"
                  color="primary"
                  onClick={() => setSummaryJsonExpanded(!summaryJsonExpanded)}
                  size="small"
                >
                  {summaryJsonExpanded ? 'Collapse All' : 'Expand All'}
                </Button>
              )}
            </Box>
          </Box>

          {isLoadingSummary ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : summaryError ? (
            <Typography color="error" variant="body2" sx={{ mb: 2 }}>
              {summaryError}
            </Typography>
          ) : (
            <>
              {(() => {
                try {
                  const jsonData = JSON.parse(flowSummary);
                  return (
                    <Box sx={{
                      mb: '8px',
                      border: `1px solid ${user?.theme === 'dark' ? '#475467' : '#EAECF0'
                        }`,
                      borderRadius: '4px',
                      p: 2,
                      maxHeight: '300px',
                      overflow: 'auto'
                    }}>
                      <JsonView
                        src={jsonData}
                        dark={user?.theme === 'dark'}
                        collapsed={summaryJsonExpanded ? false : true}
                        onEdit={(edit) => {
                          try {
                            const updatedSrc = { ...jsonData };

                            let current = updatedSrc;
                            const path = [...edit.parentPath];

                            if (path.length > 0) {
                              for (let i = 0; i < path.length; i++) {
                                current = current[path[i]];
                              }
                            }

                            current[edit.indexOrName] = edit.newValue;

                            const updatedJson = JSON.stringify(updatedSrc, null, 2);
                            setFlowSummary(updatedJson);
                            setUserEdited(updatedJson !== originalSummary);

                            onChange({
                              target: {
                                name: 'summary',
                                value: updatedJson
                              }
                            });
                          } catch (e) {
                            console.error('Error updating JSON:', e);
                          }
                        }}
                        editable={true}
                      />
                    </Box>
                  );
                } catch (e) {
                  return (
                    <TextField
                      id="flowSummary"
                      name="flowSummary"
                      value={flowSummary}
                      fullWidth
                      multiline
                      rows={4}
                      variant="outlined"
                      onChange={(e) => {
                        setFlowSummary(e.target.value);
                        setUserEdited(e.target.value !== originalSummary);
                        onChange({
                          target: {
                            name: 'summary',
                            value: e.target.value
                          }
                        });
                      }}
                      InputProps={{
                        readOnly: false,
                        style: {
                          color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                        },
                      }}
                      placeholder="Enter or generate a summary for this flow..."
                      sx={{
                        mb: '8px',
                        bgcolor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                        border: `1px solid ${user?.theme === 'dark' ? '#475467' : '#EAECF0'
                          }`,
                      }}
                    />
                  );
                }
              })()}
              {flowSummary && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                  <Button
                    variant="text"
                    color="secondary"
                    onClick={() => setShowJson(!showJson)}
                    size="small"
                  >
                    {showJson ? 'Hide Flow JSON' : 'Show Flow JSON'}
                  </Button>
                  <Button
                    variant="text"
                    color="primary"
                    onClick={generateFlowSummary}
                    size="small"
                    disabled={isLoadingSummary}
                  >
                    {isLoadingSummary ? 'Generating...' : 'Regenerate'}
                  </Button>
                </Box>
              )}
              {!flowSummary && !isLoadingSummary && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                  <Button
                    variant="text"
                    color="secondary"
                    onClick={() => setShowJson(!showJson)}
                    size="small"
                  >
                    {showJson ? 'Hide Flow JSON' : 'Show Flow JSON'}
                  </Button>
                  <Button
                    variant="text"
                    color="primary"
                    onClick={generateFlowSummary}
                    size="small"
                  >
                    Generate
                  </Button>
                </Box>
              )}
            </>
          )}
        </Box>

        {showJson && (
          <Box sx={{ mt: '16px' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: '8px' }}>
              <FormLabel
                sx={{
                  color: '#475467',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                htmlFor="flowJson"
              >
                Flow JSON
                <IconButtonTooltip title="Copy Flow JSON" onClick={copyFlowJson}>
                  <Copy size={14} color="#475467" />
                </IconButtonTooltip>
              </FormLabel>
              <Box sx={{ display: 'flex', gap: 1 }}></Box>
            </Box>

            <Box sx={{
              mb: '8px',
              border: `1px solid ${user?.theme === 'dark' ? '#475467' : '#EAECF0'
                }`,
              borderRadius: '4px',
              p: 2,
              maxHeight: '300px',
              overflow: 'auto'
            }}>
              {(() => {
                try {
                  return (
                    <JsonView
                      src={JSON.parse(flowJson)}
                      dark={user?.theme === 'dark'}
                      collapsed={false}
                    />
                  );
                } catch (e) {
                  return (
                    <Typography color="error">
                      Error parsing JSON: {(e as Error).message}
                    </Typography>
                  );
                }
              })()}
            </Box>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          mt: '26px',
        }}
      >
        <Label isAdvanced labelName="General Settings:" />
        <Divider
          sx={{
            mb: '8px',
            mt: '8px',
          }}
        />
        <FormControlLabel
          name="generateSessionId"
          onChange={onChange}
          checked={formData.generateSessionId}
          control={<Switch />}
          sx={{
            ml: 0,
            color: '#475467',
          }}
          labelPlacement="start"
          label="Allow passing in custom ID for Chat Sessions"
        />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FormLabel
            sx={{
              mr: '8px',
              color: '#475467',
            }}
            htmlFor="selectedChat"
          >
            Session ID:
          </FormLabel>
          <Select
            name="selectedChat"
            value={
              activeSession?.id || 'default'
            }
            onChange={(e) => {
              setActiveSession(e.target.value);
              setTimeout(() => {
                onChange(e);
              }, 50);
            }}
            size="small"
            sx={{
              fontWeight: 600,
              backgroundColor: '#FFF',
              '& .MuiList-root': {
                backgroundColor:
                  user?.theme === 'dark' ? '#101828' : '#F9FAFB',
              },
              '& .MuiSvgIcon-root': {
                color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
              },
              borderRadius: '8px',
              border: 'none',
              boxShadow: 'none',
              '.MuiOutlinedInput-notchedOutline': { border: 0 },
              bgcolor: user?.theme === 'dark' ? '#101828' : '#FFF',
              color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  '& .Mui-selected': {
                    backgroundColor:
                      user?.theme === 'dark' ? '#667085' : '#D0D5DD',
                  },
                  backgroundColor:
                    user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                },
              },
            }}
          >
            {sessions.map((session) => (
              <MenuItem key={session.id} value={session.id}>
                {session.name}
              </MenuItem>
            ))}
          </Select>

          <IconButtonTooltip
            title={'Create new agent session'}
            onClick={() => setShowAgentSessionModal(true)}
          >
            <PlusCircle
              size={20}
              color={user?.theme === 'dark' ? '#D0D5DD' : 'black'}
            />
          </IconButtonTooltip>
        </Box>

        <Box>
          <FormLabel
            sx={{
              mr: '8px',
              color: '#475467',
            }}
            htmlFor="selectedNode"
          >
            Target input Node (for email and webhook calls)
          </FormLabel>
          <Select
            name="selectedNode"
            value={
              formData?.selectedNode || nodeOptionsApiInput[0]?.value || 'none'
            }
            onChange={onChange}
            sx={{
              mt: '2px',
            }}
          >
            <MenuItem value={'none'} disabled>
              Choose API Input Node
            </MenuItem>
            {nodeOptionsApiInput?.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option?.label}
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Box>

      <Box sx={{ mt: '26px' }}>
        <Label
          sx={{
            mb: '16px',
          }}
          labelName="Email Calling:"
          isAdvanced
        />
        <Divider
          sx={{
            mb: '8px',
            mt: '8px',
          }}
        />
        <Box>
          <FormControlLabel
            name="showEmailCalling"
            onChange={onChange}
            checked={formData.showEmailCalling}
            control={<Switch />}
            sx={{
              ml: 0,
              color: '#475467',
            }}
            labelPlacement="start"
            label="Turn On Email triggering"
          />
        </Box>
        {formData.showEmailCalling && (
          <>
            <Typography
              sx={{
                color: '#475467',
                fontSize: '16px',
                mb: '4px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              Flow Email:{' '}
              <span
                style={{
                  color: '#98A2B3',
                }}
              >
                {activeFlow?.id}@{process.env.REACT_APP_FLOW_EMAIL}
              </span>
              <IconButtonTooltip
                title="Copy Flow Email"
                onClick={copyFlowEmail}
              >
                <Copy size={14} />
              </IconButtonTooltip>
            </Typography>

            <Box>
              <FormControlLabel
                name="isPublic"
                onChange={(e: any) => {
                  onChangeIsPublicOrWebhook?.({
                    isPublic: !isPublic,
                  });
                }}
                checked={isPublic}
                control={<Switch />}
                sx={{
                  ml: 0,
                  color: '#475467',
                }}
                labelPlacement="start"
                label="Allow emails from any address (not just yours)"
              />
            </Box>
          </>
        )}
      </Box>

      <Box sx={{ mt: '26px' }}>
        <Label isAdvanced labelName="Webhook Calling:" />
        <Divider
          sx={{
            mb: '8px',
            mt: '8px',
          }}
        />

        <Box>
          <FormControlLabel
            name="isWebhook"
            onChange={(e: any) => {
              onChangeIsPublicOrWebhook?.({
                webhook: !isWebhook,
              });
            }}
            checked={isWebhook}
            control={<Switch />}
            sx={{
              ml: 0,
              color: '#475467',
            }}
            labelPlacement="start"
            label="Turn on webhook"
          />
        </Box>
        {isWebhook && (
          <>
            <Typography
              sx={{
                color: '#475467',
                fontSize: '16px',
                mb: '4px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              Flow Webhook URL:{' '}
              <span
                style={{
                  color: '#98A2B3',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: 'inline-block',
                  maxWidth: '500px',
                }}
              >
                {process.env.REACT_APP_API_URL}/flux/api-v2-webhook?flowId=
                {activeFlow?.id}
              </span>
              <IconButtonTooltip
                title="Copy Webhook URL"
                onClick={copyWebhookUrl}
              >
                <Copy size={14} />
              </IconButtonTooltip>
            </Typography>
            <Typography
              sx={{
                mt: '8px',
                color: '#475467',
                fontSize: '12px',
              }}
              variant={'body1'}
            >
              Use this URL to call the flow from a Webhook. Any body of
              information you pass will be passing into the Data Input node
              selected under General Settings
            </Typography>
          </>
        )}
      </Box>

      <Box sx={{ mt: '26px' }}>
        <Label isAdvanced labelName="API Calling:" />
        <Divider
          sx={{
            mb: '8px',
            mt: '8px',
          }}
        />
        <Box>
          <FormControlLabel
            name="showApiCalling"
            onChange={onChange}
            checked={formData.showApiCalling}
            control={<Switch />}
            sx={{
              ml: 0,
              color: '#475467',
            }}
            labelPlacement="start"
            label="Turn on API Calling"
          />
        </Box>
        {formData.showApiCalling && (
          <>
            <Typography
              sx={{
                color: '#475467',
                fontSize: '16px',
                mb: '4px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              <span>Flow API URL:</span>
              <span
                style={{
                  color: '#98A2B3',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: 'inline-block',
                  maxWidth: '500px',
                }}
              >
                {generateLinkForFlowId}
              </span>
              <IconButtonTooltip
                title="Copy API URL"
                onClick={() => {
                  navigator.clipboard.writeText(generateLinkForFlowId!);
                }}
              >
                <Copy size={14} />
              </IconButtonTooltip>
            </Typography>
            <Typography
              sx={{
                mt: '8px',
                mb: '24px',
                color: '#475467',
                fontSize: '12px',
              }}
              variant={'body1'}
            >
              You can use the following link to access the API for the flow.
            </Typography>

            <>
              <Typography
                sx={{
                  color: '#475467',
                  fontSize: '16px',
                  mb: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                API Auth:
                <span
                  style={{
                    color: '#98A2B3',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: 'inline-block',
                    maxWidth: '500px',
                  }}
                >
                  api-key: {apiKey}
                </span>
                <IconButtonTooltip
                  title="Copy Api Key"
                  onClick={() => {
                    navigator.clipboard.writeText(apiKey!);
                  }}
                >
                  <Copy size={14} />
                </IconButtonTooltip>
              </Typography>
              <Typography
                sx={{
                  mt: '8px',
                  mb: '24px',
                  color: '#475467',
                  fontSize: '12px',
                }}
                variant={'body1'}
              >
                Use the following API Key to authenticate the API request.
              </Typography>

            </>
            <Box>
              <Typography
                sx={{
                  color: '#475467',
                  fontSize: '16px',
                  mb: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}
              >
                JSON Body Call Example
                <IconButtonTooltip title="Copy JSON Body Example" onClick={() => {
                  try {
                    navigator.clipboard.writeText(JSON.stringify(JSON.parse(config), null, 2));
                    setNotification({
                      message: 'JSON Body Example copied to clipboard',
                      type: 'success',
                    });
                  } catch (e) {
                    setNotification({
                      message: 'Invalid JSON',
                      type: 'error',
                    });
                  }
                }}>
                  <Copy size={14} color="#475467" />
                </IconButtonTooltip>
              </Typography>
              <Box sx={{
                mb: '8px',
                border: `1px solid ${user?.theme === 'dark' ? '#475467' : '#EAECF0'}`,
                borderRadius: '4px',
                p: 2,
                maxHeight: '150px',
                overflow: 'auto',
                bgcolor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                fontFamily: 'monospace',
                fontSize: '13px',
                color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
              }}>
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                  {(() => {
                    try {
                      return JSON.stringify(JSON.parse(config), null, 2);
                    } catch (e) {
                      return 'Invalid JSON';
                    }
                  })()}
                </pre>
              </Box>
            </Box>
            <Typography
              sx={{
                mt: '8px',
                color: '#475467',
                fontSize: '12px',
              }}
              variant={'body1'}
            >
              Example JSON body to use when calling the flow.
            </Typography>
            <Button
              variant="text"
              color="secondary"
              onClick={() => setShowFilesExample(!showFilesExample)}
              size="small"
              sx={{ mb: '8px' }}
            >
              {showFilesExample ? 'Hide Files Example' : 'Show Files Example'}
            </Button>
            {showFilesExample && (
              <>
                <Typography
                  sx={{
                    color: '#475467',
                    fontSize: '16px',
                    mb: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  JSON Body Call Example With Files:
                  <IconButtonTooltip title="Copy JSON Body Example With Files" onClick={() => {
                    try {
                      navigator.clipboard.writeText(JSON.stringify(JSON.parse(configFull), null, 2));
                      setNotification({
                        message: 'JSON Body Example With Files copied to clipboard',
                        type: 'success',
                      });
                    } catch (e) {
                      setNotification({
                        message: 'Invalid JSON',
                        type: 'error',
                      });
                    }
                  }}>
                    <Copy size={14} color="#475467" />
                  </IconButtonTooltip>
                </Typography>
                <Box sx={{
                  mb: '8px',
                  border: `1px solid ${user?.theme === 'dark' ? '#475467' : '#EAECF0'}`,
                  borderRadius: '4px',
                  p: 2,
                  maxHeight: '150px',
                  overflow: 'auto',
                  bgcolor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
                  fontFamily: 'monospace',
                  fontSize: '13px',
                  color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
                }}>
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                    {(() => {
                      try {
                        return JSON.stringify(JSON.parse(configFull), null, 2);
                      } catch (e) {
                        return 'Invalid JSON';
                      }
                    })()}
                  </pre>
                </Box>
              </>
            )}

          </>
        )}
      </Box>

      <SmallMemorySessionModal
        sessionName={sessionName}
        setSessionName={setSessionName}
        show={showAgentSessionModal}
        onCreateNewUserSession={onCreateNewSession}
        onHide={() => setShowAgentSessionModal(false)}
      />
    </KitModal>
  );
};

export default SettingsModal;
